import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Building2, CircleAlert, Plus } from 'lucide-react';
import { useState } from 'react';

import { Button } from '../components/Button';
import { EmptyState } from '../components/EmptyState';
import { PageHeader } from '../components/PageHeader';
import { Pagination } from '../components/Pagination';
import { SearchInput } from '../components/SearchInput';
import { StatusBadge } from '../components/StatusBadge';
import { useAuth } from '../features/auth/AuthProvider';
import {
  clientsKey,
  formatDocument,
  getClient,
  listClients,
  situationLabels,
  situationTone,
  type Client,
  type Site,
} from '../features/inventory/api';
import { ClientDrawer } from '../features/inventory/ClientDrawer';
import { SiteDrawer } from '../features/inventory/SiteDrawer';
import { readPaging } from '../lib/paging';
import { useUrlState } from '../lib/useUrlState';

/** The client drawer, or an account of that client (back to the client on close). */
type DrawerState =
  { kind: 'client'; client?: Client } | { kind: 'account'; client: Client; site?: Site } | null;

/** Clients of the organisation and their accounts (ADR-026). */
export function ClientsPage() {
  const { request, can } = useAuth();
  const [params, update] = useUrlState();
  const { page, size } = readPaging(params);
  const q = params.get('q') ?? '';
  const canManage = can('clients.manage');
  const [drawer, setDrawer] = useState<DrawerState>(null);

  const clients = useQuery({
    queryKey: [...clientsKey, { q, page, size }],
    queryFn: () => listClients(request, { q, page, size }),
    placeholderData: keepPreviousData,
  });

  /** Back to the client, refreshed (its counts may have changed). */
  const backTo = async (c: Client) => {
    try {
      const fresh = await getClient(request, c.id);
      setDrawer({ kind: 'client', client: fresh });
    } catch {
      setDrawer(null);
    }
  };

  return (
    <>
      <PageHeader
        title="Clientes"
        description="Clientes da empresa e as contas (locais monitorados) de cada um."
        actions={
          canManage && (
            <Button
              variant="primary"
              onClick={() => {
                setDrawer({ kind: 'client' });
              }}
            >
              <Plus aria-hidden="true" className="size-4" strokeWidth={2} />
              Novo cliente
            </Button>
          )
        }
      />
      <div className="space-y-3 p-4 sm:p-6">
        <div role="search">
          <SearchInput
            label="Buscar por nome, CPF/CNPJ ou conta"
            value={q}
            onChange={(v) => {
              update({ q: v });
            }}
          />
        </div>

        <div className="overflow-hidden rounded-lg border border-line bg-surface">
          {clients.isError && !clients.data ? (
            <EmptyState
              tone="crit"
              icon={CircleAlert}
              title="Não foi possível carregar os clientes"
              description="Verifique a conexão com o servidor e tente novamente."
              action={
                <Button variant="primary" onClick={() => void clients.refetch()}>
                  Tentar novamente
                </Button>
              }
            />
          ) : clients.data?.items.length === 0 ? (
            <EmptyState
              icon={Building2}
              title={q ? 'Nenhum cliente encontrado' : 'Nenhum cliente cadastrado'}
              description={
                q
                  ? 'Nenhum cliente corresponde à busca.'
                  : 'Cadastre os clientes e as contas deles para organizar câmeras e gravadores.'
              }
              {...(canManage && !q
                ? {
                    action: (
                      <Button
                        variant="primary"
                        onClick={() => {
                          setDrawer({ kind: 'client' });
                        }}
                      >
                        Cadastrar o primeiro cliente
                      </Button>
                    ),
                  }
                : {})}
            />
          ) : (
            <div className="overflow-x-auto">
              <table
                className="w-full min-w-[640px] text-left text-[13px]"
                aria-busy={clients.isFetching}
              >
                <caption className="sr-only">Clientes da organização</caption>
                <thead className="text-2xs tracking-wide text-fg-subtle uppercase">
                  <tr className="border-b border-line">
                    <th scope="col" className="px-4 py-2 font-medium">
                      Cliente
                    </th>
                    <th scope="col" className="px-4 py-2 font-medium">
                      Situação
                    </th>
                    <th scope="col" className="px-4 py-2 text-right font-medium">
                      Contas
                    </th>
                    <th scope="col" className="px-4 py-2 text-right font-medium">
                      Dispositivos
                    </th>
                    <th scope="col" className="w-px px-4 py-2">
                      <span className="sr-only">Ações</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {!clients.data
                    ? Array.from({ length: 5 }, (_, i) => (
                        <tr
                          key={i}
                          aria-hidden="true"
                          className="border-b border-line last:border-0"
                        >
                          {['w-48', 'w-16', 'ml-auto w-8', 'ml-auto w-8', 'w-10'].map((w) => (
                            <td key={w} className="px-4 py-3.5">
                              <div className={`h-3 animate-pulse rounded bg-surface-hover ${w}`} />
                            </td>
                          ))}
                        </tr>
                      ))
                    : clients.data.items.map((c) => (
                        <tr
                          key={c.id}
                          className="border-b border-line last:border-0 hover:bg-surface-hover/50"
                        >
                          <td className="px-4 py-2.5">
                            <div className="font-medium text-fg">{c.name}</div>
                            <div className="font-mono text-xs text-fg-subtle">
                              {formatDocument(c.document) || '—'}
                            </div>
                          </td>
                          <td className="px-4 py-2.5">
                            <StatusBadge tone={situationTone[c.status]}>
                              {situationLabels[c.status]}
                            </StatusBadge>
                          </td>
                          <td className="px-4 py-2.5 text-right font-mono text-xs text-fg-muted tabular-nums">
                            {c.account_count}
                          </td>
                          <td className="px-4 py-2.5 text-right font-mono text-xs text-fg-muted tabular-nums">
                            {c.device_count}
                          </td>
                          <td className="px-4 py-2.5 text-right">
                            <Button
                              variant="ghost"
                              className="h-7"
                              aria-label={`${canManage ? 'Editar' : 'Ver'} ${c.name}`}
                              onClick={() => {
                                setDrawer({ kind: 'client', client: c });
                              }}
                            >
                              {canManage ? 'Editar' : 'Ver'}
                            </Button>
                          </td>
                        </tr>
                      ))}
                </tbody>
              </table>
            </div>
          )}
          {clients.data && clients.data.total > 0 && (
            <Pagination
              page={page}
              size={size}
              total={clients.data.total}
              onPage={(p) => {
                update({ page: String(p) });
              }}
              onSize={(s) => {
                update({ size: String(s) });
              }}
            />
          )}
        </div>
      </div>
      {drawer?.kind === 'client' && (
        <ClientDrawer
          key={drawer.client?.id ?? 'new'}
          {...(drawer.client ? { client: drawer.client } : {})}
          canManage={canManage}
          onClose={() => {
            setDrawer(null);
          }}
          onOpen={(c) => {
            setDrawer({ kind: 'client', client: c });
          }}
          onAccount={(site) => {
            if (drawer.client)
              setDrawer({ kind: 'account', client: drawer.client, ...(site ? { site } : {}) });
          }}
        />
      )}
      {drawer?.kind === 'account' && (
        <SiteDrawer
          {...(drawer.site ? { site: drawer.site } : {})}
          clientId={drawer.client.id}
          canManage={canManage}
          onClose={() => {
            void backTo(drawer.client);
          }}
        />
      )}
    </>
  );
}
